import { DateTime } from "luxon";
import { createHash } from "crypto";
import { PouchDoc } from "./data-model/pouch";

/*
Telemetry data types can be saved to disk either under:

mix/event/<time>-<insert_id> - for those that are stored locally to be sent later (i.e. when offline)
mix-server/event/<time>-<insert_id> - for those that are stored to be sent server-side (because suspected adblocker)
*/
export interface TelemetryData_Base extends PouchDoc {
};

export function isTelemetryDataServerSide(data: TelemetryData_Base): boolean {
  return data._id.startsWith("mix-server/");
}

// Event with properties
export interface TelemetryEvent extends TelemetryData_Base {
  event: string;
  properties: {
    '$insert_id': string; // needed for MixPanel to dedupe events
    '$device_id': string | undefined;
    '$user_id': string | undefined;
    // ip for mixpanel geolocation: a valid ip pins the event's geo, MIXPANEL_SKIP_GEO_IP
    // disables the geo update for this event. absent = mixpanel uses the request source ip
    ip: string;
    time: number; // unix time epoch in milliseconds

    [key: string]: any;
  };
};
// sentinel passed as $ip (user state) or properties.ip (event) to tell mixpanel to
// skip ip-based geolocation for this call. use whenever no real client ip is available —
// otherwise mixpanel falls back to the request source ip, which for us is the cloud run
// egress in us-central1 (physically council bluffs / iowa) and would stamp every affected
// profile / event with that location.
export const MIXPANEL_SKIP_GEO_IP = '0';

/**
 * TEST-TRAFFIC EXCLUSION (#4098).
 *
 * We have two kinds of test traffic, and before #4098 they had no marker in common:
 *
 * - Web / e2e test traffic. The browser is sent to the app with `utm_source=e2e-test`
 *   in the URL, so every CLIENT event carries that value and boards excluded it with
 *   the filter `utm_source != e2e-test`.
 * - Server-side test purchases. Apple sandbox notifications, Google Play test
 *   purchases, PayPro test orders and Paddle sandbox grants. These events are emitted
 *   by the SERVER from a payment webhook, and a server event only carries `utm_source`
 *   if the buyer's user doc happens to have it stored. Internal test accounts buying
 *   through Paddle or the stores never had it stored, so the `utm_source` filter passed
 *   them straight through and they were counted as REAL trials on every activation board.
 *
 * The fix is ONE marker that both kinds carry: `is_test_traffic: true`, written on the
 * event AND on the user profile. Every board applies a single rule — exclude
 * `is_test_traffic is true` — and no board author has to remember two filters.
 *
 * The value is a real boolean (never the string `'true'`), so a Mixpanel "is true"
 * filter matches it.
 *
 * The marker is only ever set to TRUE, never to false, so a real customer normally has
 * no such property at all. It is NOT, however, impossible for a real customer to be
 * marked: the PayPro test-order detector is a price-shape heuristic, so a misfire there
 * would mark a real buyer. That is why the marking sites log — see the WARNING in
 * design-docs/telemetry.md ("Test-traffic exclusion rule") for how to spot and clear it.
 *
 * See design-docs/telemetry.md, section "Test-traffic exclusion rule".
 */
export const IS_TEST_TRAFFIC_PROPERTY = 'is_test_traffic';

/** The `utm_source` value that marks a browser session as e2e / manual test traffic.
 * The server reads the same value to pick the Paddle / PayPro SANDBOX environment
 * instead of the live one — see `isE2ETestUtmSource` for the shared predicate that
 * keeps those two readings identical. */
export const E2E_TEST_UTM_SOURCE = 'e2e-test';

/** The shape every caller reads the e2e marker out of: the tracking bag the client
 * builds from the URL, and the same bag once it is stored on the user doc. */
export interface TrackingDataWithUtmSource {
  utm_source?: string;
}

/** Is this `utm_source` value the e2e test marker? The single source of truth for that
 * question — used both to decide the test-traffic marker AND to pick the sandbox payment
 * environment, so the two readings can never drift apart. */
export function isE2ETestUtmSource(utmSource: string | undefined): boolean {
  return utmSource === E2E_TEST_UTM_SOURCE;
}

/**
 * Is this session / buyer test traffic, judged from their tracking data?
 *
 * Takes the whole tracking bag rather than the bare value on purpose: the field name
 * `utm_source` then lives in ONE tested place. The client's bag is untyped (`any`), so a
 * typo at the call site would otherwise silently un-mark every client event with no test
 * failing.
 *
 * Two independent signals, either one is enough:
 * - the tracking data's `utm_source` is the e2e marker (the session was opened with
 *   `?utm_source=e2e-test`, or the buyer has that stored on their user doc);
 * - `isE2ESession` — the app is running under an E2E test run. The E2E harness sets its
 *   session flag on EVERY navigation, while only some of those navigations also carry the
 *   utm parameter, so reading the flag marks the whole run rather than just those pages.
 *   (Server callers pass `false` — there is no browser session on the server.)
 *
 * Kept pure (the caller passes both signals in) so it can be unit-tested without a browser.
 */
export function isTestTrafficSession(
  trackingData: TrackingDataWithUtmSource | undefined,
  isE2ESession: boolean,
): boolean {
  return isE2ESession || isE2ETestUtmSource(trackingData?.utm_source);
}

/**
 * TEAM TEST ACCOUNTS (#5043 follow-up).
 *
 * A check on the LIVE app (a release check, a real-card trial) cannot use the
 * `e2e-test` marker, because that marker also switches the checkout to the sandbox.
 * So the team's own accounts are recognised by email instead.
 *
 * These lists copy the `$email` rules of the Mixpanel cohort "Internal / Test users"
 * (prod cohort id 4752700), as read on 2026-10-01. The cohort excludes these people
 * from cohort-filtered boards; this copy carries the rule to the places a cohort cannot
 * reach: the `is_test_traffic` marker and the choice of the Meta test dataset. Change
 * the cohort and these lists TOGETHER — nothing checks that they still match, because
 * reading the cohort needs Mixpanel credentials.
 *
 * One deliberate difference: two cohort rules are anchored here, because as plain
 * substrings they also match real customers (`tin.berger` inside martin.berger@,
 * `bell-coaching.com` inside campbell-coaching.com). Here a match also costs a real
 * customer their ad attribution, for good, so the code is stricter than the cohort.
 *
 * Deliberately NOT copied from the cohort: its two `$name` rules (the same people's
 * email rules cover them, and a name is free text a customer could share), its nested
 * cohorts (lists of profiles, not rules), and its utm / sandbox / is_test_traffic rules
 * (the code above already handles those).
 *
 * See design-docs/telemetry.md, "Test-traffic exclusion rule".
 */
export const TEAM_TEST_EMAIL_CONTAINS: readonly string[] = [
  '@cyberneticfitness.app',
  '@mh-physique.app',
  '@bell-coaching.com',
  'alen.ladavac',
  'sveineven',
  'vincent.huisman',
  'annagyure',
  'ilya.poltavets',
  'test+j7l9v@payproglobal.com',
  'support@postmarkapp.com',
];

/** Cohort `contains` rules anchored to the START of the address, so a longer name that
 *  ends in the same letters (martin.berger@) is not matched. See `TEAM_TEST_EMAIL_CONTAINS`. */
export const TEAM_TEST_EMAIL_STARTS_WITH: readonly string[] = [
  'tin.berger',
];

/** The cohort's exact-match `$email` rules. See `TEAM_TEST_EMAIL_CONTAINS`. */
export const TEAM_TEST_EMAIL_EQUALS: readonly string[] = [
  'buffbeetch@gmail.com',
  'buffbrute@gmail.com',
];

/** The E2E test-account local-part convention. `isStaffOrTestEmail` matches the same
 *  convention with its own literal, so change both together. */
const E2E_TEST_EMAIL_MARKER = 'testuser.temp+';

/**
 * Is this the email of an account an E2E run created? Narrower than
 * `isTeamTestAccountEmail`: staff and tester accounts do NOT match. The ActiveCampaign
 * sync leaves these accounts out, while it still sends staff accounts so the team can
 * try the email flows on themselves.
 */
export function isE2ETestAccountEmail(email: string | undefined): boolean {
  return !!email && email.trim().toLowerCase().includes(E2E_TEST_EMAIL_MARKER);
}

/**
 * Is this the email of one of the team's own accounts (staff, testers, E2E runs)?
 *
 * Case-insensitive, like the Mixpanel cohort's `contains` filter. Such an account is
 * test traffic: its events carry `is_test_traffic` and its purchases report to the Meta
 * test dataset.
 */
export function isTeamTestAccountEmail(email: string | undefined): boolean {
  // if there is no email we cannot tell, and a real customer must never be hidden
  if (!email) {
    return false;
  }
  const lower = email.trim().toLowerCase();
  return TEAM_TEST_EMAIL_EQUALS.includes(lower)
    || TEAM_TEST_EMAIL_STARTS_WITH.some(prefix => lower.startsWith(prefix))
    || TEAM_TEST_EMAIL_CONTAINS.some(part => lower.includes(part))
    || isE2ETestAccountEmail(lower);
}

export interface TelemetryUserState extends TelemetryData_Base {
  $token?: string;
  $distinct_id: string;
  // mixpanel geolocation hint: a valid ip pins the profile's geo, MIXPANEL_SKIP_GEO_IP disables
  // the geo update for this call. absent = mixpanel uses the request source ip (never what we want
  // on server-side calls)
  $ip?: string;
  // EXACTLY ONE of these must be present
  $set?: { [key: string]: any };
  $set_once?: { [key: string]: any };
  $add?: { [key: string]: any };
  $union?: { [key: string]: any };
};

export type TelemetryData = TelemetryEvent | TelemetryUserState;

// ways to identify a Mixpanel User in an event - AT LEAST ONE of userId or deviceId must be present
export type MpUserIdentity = {
  userId?: string;
  deviceId?: string;
  // ip to attribute the event to. MIXPANEL_SKIP_GEO_IP means "don't track geo with this event",
  // typically used for server-triggered events where no client ip is available
  ip: string;
};

let uidSerial = 0;  // used to generate unique insertion ids

// prepare one telemetry event to store in our database, in format ready to send straight to MixPanel
export function encodeTelemetryEvent(
  eventName: string,
  eventProperties: { [key: string]: any },
  context: {
  } & MpUserIdentity,
): TelemetryEvent {

  let time = DateTime.utc();
  let epochTime = time.toMillis();

  let serial = uidSerial++;

  let uniqueString = `${context.userId}:${context.deviceId}:${context.ip}:${epochTime}:${serial}`;
  let insertId = createHash('sha256').update(uniqueString).digest('hex').slice(0, 36);

  const recordId = `mix/event/${epochTime}-${insertId}`;
  let record: TelemetryEvent = {
    _id: recordId,
    event: eventName,
    properties: {
      $device_id: context.deviceId,
      $user_id: context.userId,
      ip: context.ip,
      time: epochTime,
      $insert_id: insertId,

      ...eventProperties,
    }
  };

  return record;
}

// prepare one telemetry user set to store in our database, in format ready to send straight to MixPanel.
//
// THE last_modified INVARIANT — this function is where it is first stamped, for every payload.
// the server then overwrites it with arrival time (restampLastModifiedOnArrival in
// server/functions/src/helpers/mixpanel-helpers.ts), so a change to the rule belongs in both.
// Every mixpanel people `$set` carries a fresh last_modified (epoch seconds), because the hourly
// ActiveCampaign sync finds changed contacts by asking mixpanel for `last_modified > <cursor>`:
// a people write that does not advance it is never seen, and ActiveCampaign drifts away from
// mixpanel with nothing failing. Every people payload on both the client and the server is built
// here, so every write site is covered including ones nobody has written yet.
//
// Only the `set` form is stamped, and the other three must never be — a `set_once` last_modified
// would freeze at its first value and hide that profile from the sync for good.
//
// The full rule, and what it costs to get wrong: design-docs/telemetry.md, "The last_modified
// chokepoint".
export function encodeTelemetryUserState(
  userId: string,
  changes: {
    set: { [key: string]: any },
  } | {
    set_once: { [key: string]: any },
  } | {
    add: { [key: string]: any },
  } | {
    union: { [key: string]: any },
  }
): TelemetryUserState {
  let time = DateTime.utc();
  let epochTime = time.toMillis();

  // user state updates don't require insertid per-se, but we need it to prevent conflicts in _id for couch storage
  let serial = uidSerial++;
  let uniqueString = `${userId}:${epochTime}:${serial}`;
  let insertId = createHash('sha256').update(uniqueString).digest('hex').slice(0, 36);

  // stamp the last_modified invariant onto the `set` form — see this function's doc comment.
  // a new object is built rather than the caller's being mutated, and last_modified is written
  // last so this value always wins over one a caller happened to pass in.
  let setChanges = changes['set'] === undefined
    ? undefined
    : { ...changes['set'], last_modified: Math.floor(epochTime / 1000) };

  let packet: TelemetryUserState = {
    _id: `mix/user-${epochTime}-${insertId}`,
    $distinct_id: userId,
    $set: setChanges,
    $set_once: changes['set_once'],
    $add: changes['add'],
    $union: changes['union'],
  };

  // delete unused ones and make sure exactly one is used
  let operationCount = 0;
  for (let key of ["$set", "$set_once", "$add", "$union"]) {
    if (packet[key] === undefined) {
      delete packet[key];
    } else {
      operationCount++;
      // transcribe union so that each field in it is a single-element array (why? because MixPanel...¯\_(ツ)_/¯)
      if (key === "$union") {
        for (let k in packet.$union) {
          packet.$union[k] = [packet.$union[k]];
        }
      }
    }
  }

  // exactly one of these must be present
  if (operationCount !== 1) {
    throw new Error(`Exactly one of set, set_once, add, or append must be present in a telemetry user state packet`);
  }

  return packet;
}
